import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Menu, ExternalLink, Download } from 'lucide-react';
import { askQuestionStream, deleteChatRoom, fetchSourcePdf, getChatRoomHistory, listChatRooms, submitFeedback } from '../services/api';
import { triggerQuotaRefresh } from './QuotaCard';
import { useAuth } from '../contexts/AuthContext';
import { isMfaDemoEnabled } from '../services/auth';
import { hasDashboard } from '../services/roles';
import translations from '../i18n/translations';
import { toggleTheme } from '../theme';
import Sidebar from './Sidebar';
import MessageList from './MessageList';
import { isApiPdfUrl } from './MessageItem';
import faqQuestions from '../i18n/faq';
import ChatInput from './ChatInput';
import { appendMessageChunk, updateMessageAtIndex, updateMessageById } from './chatMessageState';

// pdf.js is large; load it only when a source is previewed.
const SourcePdfViewer = lazy(() => import('./SourcePdfViewer'));

// バックエンドと同じ形式に揃え、復元時に不正なIDをAPIへ渡さないようにする。
const SESSION_ID_PATTERN = /^[a-zA-Z0-9-_]{8,64}$/;
const DEFAULT_CHAT_TITLES = new Set(['New Chat', '新しいチャット']);

// Page of the first cited passage, so even the fallback viewer opens where the answer came from.
function firstPassagePage(source) {
  return (source?.passages || []).find((passage) => Number.isInteger(passage?.page))?.page || null;
}

function createSessionId() {
  return crypto.randomUUID();
}

function buildChatTitle(chatMessages, fallbackTitle) {
  const firstUserMessage = chatMessages.find(message => message.type === 'user')?.text?.trim();
  if (!firstUserMessage) return fallbackTitle;
  const compactTitle = firstUserMessage.replace(/\s+/g, ' ');
  return compactTitle.length > 32 ? `${compactTitle.slice(0, 32)}...` : compactTitle;
}

function resolveChatTitle(title, messages, fallbackTitle) {
  const normalizedTitle = typeof title === 'string' ? title.trim() : '';
  if (normalizedTitle && normalizedTitle !== fallbackTitle && !DEFAULT_CHAT_TITLES.has(normalizedTitle)) {
    return normalizedTitle;
  }
  return buildChatTitle(messages, fallbackTitle);
}

function detectPromptLanguage(text, fallbackLanguage) {
  const hasJapanese = /[\u3040-\u30ff\u3400-\u9fff]/.test(text);
  const hasEnglish = /[A-Za-z]/.test(text);

  if (hasEnglish && !hasJapanese) return 'EN';
  if (hasJapanese && !hasEnglish) return '日本語';

  return fallbackLanguage;
}

function normalizeServerMessages(serverMessages) {
  return Array.isArray(serverMessages)
    ? serverMessages.map(message => ({
        ...message,
        messageId: message.message_id || message.messageId,
      }))
    : [];
}

function normalizeServerChatRoom(chat, fallbackTitle) {
  // バックエンドのsnake_caseレスポンスをフロントのチャット形式へ変換する。
  // 一覧はメタデータのみ。履歴はルームを開いた時に取得する(historyLoaded=false)。
  const sessionIdCandidate = chat.session_id || chat.sessionId || chat.id;
  const sessionId = typeof sessionIdCandidate === 'string' && SESSION_ID_PATTERN.test(sessionIdCandidate)
    ? sessionIdCandidate
    : createSessionId();

  return {
    id: chat.id || sessionId,
    sessionId,
    title: resolveChatTitle(chat.title, [], fallbackTitle),
    ttl: chat.ttl,
    updatedAt: chat.updated_at || chat.updatedAt,
    messages: [],
    historyLoaded: false,
    draft: '',
  };
}

function mergeServerChatRooms(serverRooms, previousChats, fallbackTitle) {
  if (!Array.isArray(serverRooms)) return previousChats;

  const previousBySessionId = new Map();
  previousChats.forEach(chat => {
    const key = chat.sessionId || chat.id;
    if (key) previousBySessionId.set(key, chat);
  });

  return serverRooms.map(room => {
    const normalizedRoom = normalizeServerChatRoom(room, fallbackTitle);
    const previousChat = previousBySessionId.get(normalizedRoom.sessionId) || previousBySessionId.get(normalizedRoom.id);

    return previousChat
      ? {
          ...previousChat,
          id: normalizedRoom.id,
          sessionId: normalizedRoom.sessionId,
          title: normalizedRoom.title,
          ttl: normalizedRoom.ttl,
          updatedAt: normalizedRoom.updatedAt,
        }
      : normalizedRoom;
  });
}

export default function ChatApp() {
  const navigate = useNavigate();
  const { langCode, sessionId: routeSessionId } = useParams();
  const languagePath = langCode === 'en' ? 'en' : 'jp';
  const { user, logout } = useAuth();
  const language = languagePath === 'en' ? 'EN' : '日本語';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [chatRoomsLoaded, setChatRoomsLoaded] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('chatty_sidebar_collapsed') === 'true');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [selectedSource, setSelectedSource] = useState(null);
  // pdf.js could not open the file (e.g. blocked by CORS/CSP): fall back to the browser's viewer.
  const [pdfViewerFailed, setPdfViewerFailed] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(320);
  const [isResizingPreview, setIsResizingPreview] = useState(false);
  const resizeHandleRef = useRef(null);
  const streamBufferRef = useRef('');
  const streamFrameRef = useRef(null);
  const messagesRef = useRef(messages);
  const chatHistoryRef = useRef(chatHistory);
  const currentChatIdRef = useRef(currentChatId);
  const logoutRef = useRef(logout);
  messagesRef.current = messages;
  chatHistoryRef.current = chatHistory;
  currentChatIdRef.current = currentChatId;
  logoutRef.current = logout;

  const translation = translations[language];
  const suggestedQuestions = faqQuestions[language] || [];

  const cancelStreamBuffer = useCallback(() => {
    if (streamFrameRef.current !== null) {
      cancelAnimationFrame(streamFrameRef.current);
      streamFrameRef.current = null;
    }
    streamBufferRef.current = '';
  }, []);

  const flushStreamBuffer = useCallback((botMessageId) => {
    const buffered = streamBufferRef.current;
    cancelStreamBuffer();
    if (!buffered) return;
    setMessages(prev => appendMessageChunk(prev, botMessageId, buffered));
  }, [cancelStreamBuffer]);

  // Stop an in-flight stream when the chat screen goes away (e.g. logout)
  useEffect(() => () => {
    if (abortRef.current) { abortRef.current(); abortRef.current = null; }
    cancelStreamBuffer();
  }, [cancelStreamBuffer]);

  // --- PDF Drag State ---
  const [isDraggingPdf, setIsDraggingPdf] = useState(false);

  const getChatTitle = useCallback((chatMessages, fallbackTitle) => {
    return buildChatTitle(chatMessages, fallbackTitle);
  }, []);

  const createChatRoom = useCallback((chatMessages = []) => {
    const id = createSessionId();
    return {
      id,
      sessionId: id,
      title: translation.newChat,
      messages: chatMessages,
      historyLoaded: true,
      draft: '',
    };
  }, [translation.newChat]);

  const getChatSessionId = useCallback((chatId) => {
    const targetChatId = chatId || currentChatIdRef.current;
    if (!targetChatId) return null;
    const chat = chatHistoryRef.current.find(item => item.id === targetChatId);
    if (typeof chat?.sessionId === 'string' && SESSION_ID_PATTERN.test(chat.sessionId)) {
      return chat.sessionId;
    }
    return SESSION_ID_PATTERN.test(targetChatId) ? targetChatId : null;
  }, []);

  const buildChatPath = useCallback((targetLanguagePath = languagePath, sessionId = null) => {
    return sessionId ? `/${targetLanguagePath}/chat/${sessionId}` : `/${targetLanguagePath}`;
  }, [languagePath]);

  useEffect(() => {
    if (langCode !== 'en' && langCode !== 'jp') {
      // 想定外な言語URLは日本語チャットへ戻す
      navigate('/jp', { replace: true });
    }
  }, [langCode, navigate]);

  const changeLanguage = (nextLanguage) => {
    if (isStreaming) return;
    const nextLang = nextLanguage === 'EN' ? 'en' : 'jp';
    const activeSessionId = getChatSessionId();
    // 言語ボタン選択時にURLパスへ反映する
    navigate(buildChatPath(nextLang, activeSessionId));
  };

  const currentChatTitle = chatHistory.find(chat => chat.id === currentChatId)?.title || translation.newChat;

  const refreshChatRoomMetadata = useCallback(async () => {
    try {
      const serverRooms = await listChatRooms();
      // Preserve the active room during a transient empty response after streaming.
      if (Array.isArray(serverRooms) && serverRooms.length === 0 && currentChatIdRef.current) {
        return;
      }
      setChatHistory(prev => mergeServerChatRooms(serverRooms, prev, translation.newChat));
    } catch (error) {
      console.error('Failed to refresh chat rooms:', error);
      if (error.code === 'UNAUTHORIZED') logoutRef.current();
    }
  }, [translation.newChat]);

  // チャット履歴はバックエンドを唯一の復元元にする。
  useEffect(() => {
    let cancelled = false;

    const applyChats = (chats) => {
      setChatHistory(chats);
      setChatRoomsLoaded(true);
    };

    const loadPersistedRooms = async () => {
      setChatRoomsLoaded(false);
      try {
        const serverRooms = await listChatRooms();
        if (cancelled) return;
        if (Array.isArray(serverRooms) && serverRooms.length > 0) {
          const normalizedServerChats = serverRooms.map(room => normalizeServerChatRoom(room, translation.newChat));
          applyChats(normalizedServerChats);
        } else {
          // API成功で0件なら、DynamoDBにルームがない状態として扱う。
          applyChats([]);
        }
      } catch (error) {
        if (!cancelled) {
          if (error.code === 'UNAUTHORIZED') logoutRef.current();
          applyChats([]);
        }
      }
    };

    loadPersistedRooms();
    return () => { cancelled = true; };
  }, [translation.newChat]);

  useEffect(() => {
    if (!chatRoomsLoaded || isStreaming) return;

    if (!routeSessionId) {
      if (currentChatId) {
        setCurrentChatId(null);
        setMessages([]);
        setInput('');
      }
      return;
    }

    if (!SESSION_ID_PATTERN.test(routeSessionId)) {
      navigate(buildChatPath(languagePath), { replace: true });
      return;
    }

    const routedChat = chatHistory.find(chat => (
      chat.sessionId === routeSessionId || chat.id === routeSessionId
    ));

    if (!routedChat) {
      navigate(buildChatPath(languagePath), { replace: true });
      return;
    }

    if (currentChatId === routedChat.id) return;

    setMessages(routedChat.messages || []);
    setInput(routedChat.draft || '');
    setCurrentChatId(routedChat.id);
  }, [buildChatPath, chatHistory, chatRoomsLoaded, currentChatId, isStreaming, languagePath, navigate, routeSessionId]);

  // Keep the active room in sync with the visible message list and draft input
  useEffect(() => {
    if (!currentChatId) return;
    setChatHistory(prev => prev.map(chat => (
      chat.id === currentChatId
        ? {
            ...chat,
            // 履歴の読み込み中(メッセージ0件)は一覧のタイトルを保つ。
            title: messages.length ? getChatTitle(messages, translation.newChat) : chat.title,
            messages,
            draft: input,
          }
        : chat
    )));
  }, [currentChatId, getChatTitle, input, messages, translation.newChat]);

  // 開いたルームの履歴だけを、最初に開いた時に一度だけ取得する。
  // 失敗したルームはサイドバーから開き直すまで再試行しない(入力のたびに再取得しないため)。
  const historyRequestsRef = useRef(new Set());
  useEffect(() => {
    const chat = chatHistory.find(item => item.id === currentChatId);
    if (!chat || chat.historyLoaded !== false || historyRequestsRef.current.has(chat.id)) return;
    const chatId = chat.id;
    historyRequestsRef.current.add(chatId);

    getChatRoomHistory(chat.sessionId)
      .then(serverMessages => {
        // 開き直しで重複した要求は、先に届いた結果だけを使う。
        if (chatHistoryRef.current.find(item => item.id === chatId)?.historyLoaded) return;
        const history = normalizeServerMessages(serverMessages);
        setChatHistory(prev => prev.map(item => (
          item.id === chatId ? { ...item, messages: history, historyLoaded: true } : item
        )));
        // 読み込み中に送信したメッセージがあれば、その前に履歴を差し込む。
        if (currentChatIdRef.current === chatId) {
          setMessages(prev => [...history, ...prev]);
        }
      })
      .catch(error => {
        console.error('Failed to load chat room history:', error);
        if (error.code === 'UNAUTHORIZED') logoutRef.current();
      });
  }, [chatHistory, currentChatId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    const question = input.trim();
    const promptLanguage = detectPromptLanguage(question, language);
    let activeChatId = currentChatId;
    let activeSessionId = getChatSessionId(activeChatId);

    if (!currentChatId) {
      const newChat = createChatRoom();
      activeChatId = newChat.id;
      activeSessionId = newChat.sessionId;
      setChatHistory(prev => [newChat, ...prev]);
      setCurrentChatId(newChat.id);
    }

    if (!activeSessionId) {
      activeSessionId = createSessionId();
      setChatHistory(prev => prev.map(chat => (
        chat.id === activeChatId ? { ...chat, sessionId: activeSessionId } : chat
      )));
    }

    if (activeSessionId && routeSessionId !== activeSessionId) {
      navigate(buildChatPath(languagePath, activeSessionId), { replace: !routeSessionId });
    }

    setMessages(prev => [...prev, { type: 'user', text: question, promptLanguage }]);
    setInput('');
    setIsStreaming(true);

    const botMessageId = crypto.randomUUID();
    setMessages(prev => [...prev, { id: botMessageId, type: 'bot', text: '', isThinking: true, status: 'analyzing', promptLanguage }]);

    const statusLabels = { analyzing: translation.analyzing, searching: translation.searching, generating: translation.generating };
    const updateBotMessage = (update) => setMessages(prev => updateMessageById(prev, botMessageId, update));

    const abort = askQuestionStream(question, activeSessionId, {
      onStatus: (status) => {
        updateBotMessage(msg => ({ ...msg, status, statusLabel: statusLabels[status] || status }));
      },
      onChunk: (content) => {
        // Coalesce tokens into one state update per animation frame, so rendering
        // is paced by the display rather than by how fast tokens arrive.
        streamBufferRef.current += content;
        if (streamFrameRef.current === null) {
          streamFrameRef.current = requestAnimationFrame(() => {
            streamFrameRef.current = null;
            flushStreamBuffer(botMessageId);
          });
        }
      },
      onMetadata: (metadata) => {
        flushStreamBuffer(botMessageId);
        updateBotMessage(msg => ({
          ...msg,
          messageId: metadata.messageId,
          sources: metadata.sources || [],
          confidence: metadata.confidence,
          analysis: metadata.analysis,
        }));
      },
      onError: (err) => {
        // The error text replaces the answer, so drop anything still buffered.
        cancelStreamBuffer();
        const errorText = err.code === 'UNAUTHORIZED'
          ? 'Session expired. Please log in again.'
          : err.code === 'RATE_LIMITED'
          ? (err.message || 'Too many requests. Please wait a moment.')
          : err.code === 'SERVICE_UNAVAILABLE'
          ? translation.serviceUnavailable
          : 'Error: Unable to reach server.';
        updateBotMessage(msg => ({ ...msg, text: errorText, isThinking: false, isError: true }));
        setIsStreaming(false);
        if (err.code === 'UNAUTHORIZED') logout();
      },
      onDone: () => {
        flushStreamBuffer(botMessageId);
        updateBotMessage(msg => ({ ...msg, isThinking: false, status: undefined, statusLabel: undefined }));
        setIsStreaming(false);
        refreshChatRoomMetadata();
        // Refresh quota card after successful API call
        triggerQuotaRefresh();
      },
    });

    abortRef.current = () => { cancelStreamBuffer(); abort(); };
  }, [buildChatPath, cancelStreamBuffer, createChatRoom, currentChatId, flushStreamBuffer, getChatSessionId, input, isStreaming, language, languagePath, logout, navigate, refreshChatRoomMetadata, routeSessionId, translation]);

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => { const next = !prev; localStorage.setItem('chatty_sidebar_collapsed', next); return next; });
  };

  const startNewChat = () => {
    if (isStreaming) return;

    // Abort any ongoing streaming
    if (abortRef.current) { abortRef.current(); abortRef.current = null; }
    setIsStreaming(false);

    setCurrentChatId(null);
    setMessages([]);
    setInput('');
    setSidebarOpen(false);
    navigate(buildChatPath(languagePath));
  };

  // Reads messages through a ref so the callback identity stays stable and
  // memoized message rows aren't invalidated by every incoming chunk.
  const handleFeedback = useCallback((messageIndex, rating) => {
    const message = messagesRef.current[messageIndex];
    if (!message?.messageId) return;
    setMessages(prev => updateMessageAtIndex(prev, messageIndex, msg => ({ ...msg, feedback: rating })));
    const activeSessionId = getChatSessionId();
    if (activeSessionId) {
      submitFeedback(activeSessionId, message.messageId, rating).catch(console.error);
    }
  }, [getChatSessionId]);

  const loadChat = (chat) => {
    if (isStreaming) return;

    if (abortRef.current) { abortRef.current(); abortRef.current = null; }
    setIsStreaming(false);
    if (!chat.historyLoaded) historyRequestsRef.current.delete(chat.id);
    setMessages(chat.messages || []);
    setInput(chat.draft || '');
    setCurrentChatId(chat.id);
    setSidebarOpen(false);
    const chatSessionId = typeof chat.sessionId === 'string' && SESSION_ID_PATTERN.test(chat.sessionId)
      ? chat.sessionId
      : chat.id;
    if (SESSION_ID_PATTERN.test(chatSessionId) && routeSessionId !== chatSessionId) {
      navigate(buildChatPath(languagePath, chatSessionId));
    }
  };

  const deleteChat = (chatId, e) => {
    e?.stopPropagation?.();
    if (isStreaming) return;

    const sessionIdToDelete = getChatSessionId(chatId);
    if (sessionIdToDelete) {
      // 削除は現在のユーザーに紐づくDynamoDB側のルームにも反映する。
      deleteChatRoom(sessionIdToDelete).catch(error => {
        console.error('Failed to delete chat room:', error);
        if (error.code === 'UNAUTHORIZED') logout();
      });
    }

    if (currentChatId === chatId && abortRef.current) {
      abortRef.current();
      abortRef.current = null;
      setIsStreaming(false);
    }
    const remainingChats = chatHistory.filter(c => c.id !== chatId);
    setChatHistory(remainingChats);
    if (currentChatId === chatId) {
      setCurrentChatId(null);
      setMessages([]);
      setInput('');
      navigate(buildChatPath(languagePath), { replace: true });
    } else if (routeSessionId === sessionIdToDelete) {
      navigate(buildChatPath(languagePath), { replace: true });
    }
  };

  const handleSourceClick = useCallback(async (source) => {
    if (!source) return;

    let name = typeof source === "string" ? source : source.name || "Source";
    let url = typeof source === "object" ? source.url || "" : "";
    const isPdf = name.toLowerCase().endsWith(".pdf") || url.toLowerCase().includes(".pdf") || isApiPdfUrl(url);

    if (!url || !isPdf) return;

    if (isApiPdfUrl(url)) {
      // ponytail: blob URLs are not revoked; one small PDF per click, freed on reload.
      try {
        url = await fetchSourcePdf(url);
      } catch (err) {
        console.error('Failed to load source PDF:', err);
        return;
      }
      name = name.replace(/\.json$/i, ".pdf");
    }

    setPdfViewerFailed(false);
    setSelectedSource({
      ...(typeof source === "object" ? source : {}),
      name,
      type: "pdf",
      url,
      content: "",
    });
  }, []);

const handleDocumentDownload = useCallback(async (source) => {
    if (!source || typeof source !== "object" || !source.url) return;

    let name = source.name || "source-file";
    let href = source.url;

    if (isApiPdfUrl(href)) {
      try {
        href = await fetchSourcePdf(href);
      } catch (err) {
        console.error('Failed to load source PDF:', err);
        return;
      }
      name = name.replace(/\.json$/i, ".pdf");
    }

    const link = document.createElement("a");
    link.href = href;
    link.download = name.split("/").pop() || name;

    // Add this line to force the browser to open a new tab
    link.target = "_blank";

    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  // --- PREVIEW PANEL RESIZING ---
  const startPreviewResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingPreview(true);
    resizeHandleRef.current = e.currentTarget;
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const resizePreview = useCallback((e) => {
    if (!isResizingPreview) return;
    const newWidth = window.innerWidth - e.clientX;
    const limitedWidth = Math.min(Math.max(newWidth, 320), window.innerWidth / 2);
    setPreviewWidth(limitedWidth);
  }, [isResizingPreview]);

  const stopPreviewResize = useCallback((e) => {
    setIsResizingPreview(false);
    const handle = resizeHandleRef.current;
    if (handle && handle.releasePointerCapture && e.pointerId) {
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch (err) {}
    }
    resizeHandleRef.current = null;
    document.body.style.cursor = "default";
    document.body.style.userSelect = "auto";
  }, []);

  useEffect(() => {
    window.addEventListener("pointermove", resizePreview);
    window.addEventListener("pointerup", stopPreviewResize);
    window.addEventListener("pointercancel", stopPreviewResize);
    return () => {
      window.removeEventListener("pointermove", resizePreview);
      window.removeEventListener("pointerup", stopPreviewResize);
      window.removeEventListener("pointercancel", stopPreviewResize);
    };
  }, [resizePreview, stopPreviewResize]);


  const dragPdf = useCallback((e) => {
    if (!isDraggingPdf) return;
  }, [isDraggingPdf]);

  const stopPdfDrag = useCallback(() => {
    setIsDraggingPdf(false);
    document.body.style.cursor = "default";
    document.body.style.userSelect = "auto";
  }, []);

  useEffect(() => {
    if (isDraggingPdf) {
      window.addEventListener("pointermove", dragPdf);
      window.addEventListener("pointerup", stopPdfDrag);
      window.addEventListener("pointercancel", stopPdfDrag);
    }
    return () => {
      window.removeEventListener("pointermove", dragPdf);
      window.removeEventListener("pointerup", stopPdfDrag);
      window.removeEventListener("pointercancel", stopPdfDrag);
    };
  }, [isDraggingPdf, dragPdf, stopPdfDrag]);

  return (
    <div className="flex h-screen bg-light-bg text-light-text dark:bg-main dark:text-primary">
      <Sidebar
        chatHistory={chatHistory}
        currentChatId={currentChatId}
        showDashboard={hasDashboard(user)}
        username={user.username}
        language={language}
        translation={translation}
        isStreaming={isStreaming}
        onNewChat={startNewChat}
        onLoadChat={loadChat}
        onDeleteChat={deleteChat}
        onNavigateAdmin={() => {
          if (isStreaming) return;
          navigate(`/${languagePath}/admin/faq`);
          setSidebarOpen(false);
        }}
        onLogout={() => {
          if (isStreaming) return;
          logout();
        }}
        onToggleTheme={toggleTheme}
        onChangeLanguage={changeLanguage}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
        isMobileOpen={sidebarOpen}
        onCloseMobile={() => setSidebarOpen(false)}
      />
      <div className="flex-1 flex flex-row min-w-0">
        <div className="flex-1 flex flex-col min-w-0 transition bg-light-bg text-light-text dark:bg-main dark:text-primary">
          {/* Title bar */}
          <div className="z-10 flex h-14 flex-shrink-0 items-center gap-3 border-b px-3 transition md:px-5 border-light-border bg-light-bg text-light-text dark:border-glass dark:bg-main dark:text-primary">
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition md:hidden hover:bg-black/10 text-light-text dark:hover:bg-white/10 dark:text-primary"
              aria-label={translation.menu}
            >
              <Menu size={24} />
            </button>
            <h2 className="min-w-0 truncate text-sm font-semibold text-light-text dark:text-[#F5EFE6] md:text-base">{currentChatTitle}</h2>
            {isMfaDemoEnabled() && (
              <span className="text-xs text-light-muted dark:text-secondary">{translation.localMfaDemo}</span>
            )}
          </div>

          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-w-0 min-h-0">
              <MessageList
                key={language}
                messages={messages}
                t={translation}
                language={language}
                suggestedQuestions={suggestedQuestions}
                currentChatId={currentChatId}
                messagesEndRef={messagesEndRef}
                onFeedback={handleFeedback}
                username={user.username}
                onSourceClick={handleSourceClick}
                onDocumentDownload={handleDocumentDownload}
                setInput={setInput}
              />
            </div>

            <ChatInput
              input={input}
              onInputChange={setInput}
              onSend={handleSend}
              isStreaming={isStreaming}
              placeholder={translation.placeholder}
              sendLabel={translation.sendMessage}
            />

            <div className="px-4 pb-2 text-center">
              <p className="text-xs transition text-light-muted dark:text-[#F5EFE6]">
                {translation.disclaimer}
              </p>
            </div>
          </div>
        </div>

        {selectedSource && (
          <div
            style={{ width: `${previewWidth}px` }}
            className="relative border-l flex flex-col flex-shrink-0 bg-light-bg border-light-border text-light-text dark:bg-main dark:border-glass dark:text-primary"
          >
            <div
              ref={resizeHandleRef}
              onPointerDown={startPreviewResize}
              className="absolute left-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-400 z-50"
            />

            <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-glass">
              <span className="truncate font-semibold text-sm pr-4" title={selectedSource.name}>
                {selectedSource.name || "Source Preview"}
              </span>

              <div className="flex items-center gap-1 text-gray-500 dark:text-secondary flex-shrink-0">
                {selectedSource.url && (
                  <button
                    onClick={() => window.open(selectedSource.url, "_blank")}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink size={18} />
                  </button>
                )}

                <button
                  onClick={() => handleDocumentDownload(selectedSource)}
                  className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  title="Download"
                >
                  <Download size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-gray-100 dark:bg-main">
              {selectedSource.type === "pdf" && selectedSource.url && !pdfViewerFailed ? (
                <Suspense fallback={<p className="p-4 text-sm opacity-70">{translation.loadingPdf}</p>}>
                  <SourcePdfViewer
                    // The same signed URL can sit under several answers with different citations.
                    key={`${selectedSource.url}|${JSON.stringify(selectedSource.passages || [])}`}
                    url={selectedSource.url}
                    passages={selectedSource.passages || undefined}
                    width={Math.max(previewWidth - 32, 200)}
                    labels={translation}
                    onError={(err) => {
                      console.error('PDF viewer failed, using the browser viewer:', err);
                      setPdfViewerFailed(true);
                    }}
                  />
                </Suspense>
              ) : selectedSource.type === "pdf" && selectedSource.url ? (
                <object
                  key={selectedSource.url}
                  data={`${selectedSource.url}#${firstPassagePage(selectedSource) ? `page=${firstPassagePage(selectedSource)}&` : ""}toolbar=1&navpanes=0`}
                  type="application/pdf"
                  className="w-full h-full border-0"
                >
                  <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                    <p className="mb-2 opacity-70 dark:text-secondary">
                      Your browser does not support inline PDF viewing.
                    </p>
                    <a
                      href={selectedSource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      Click here to download the PDF
                    </a>
                  </div>
                </object>
              ) : selectedSource.content ? (
                <pre className="whitespace-pre-wrap font-mono text-sm p-4 bg-white dark:bg-main">
                  {selectedSource.content}
                </pre>
              ) : (
                <p className="opacity-70 p-4">
                  No preview content available for this source.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
